// frontend/src/components/staff/TimeLogEditModal.js
//
// Workforce — TIME-010 Admin Direct Edit modal.
//
// Surfaces from the PAY-005 timesheet-review drill-in drawer per Zuri spec
// §5.1 + §5.4 + §6.3. Tertiary [Edit] button on each TimeLog row card opens
// this modal centered over the drawer.
//
// Behaviour highlights (Zuri spec §5.4 + §6.3):
//   - Modal-on-modal stacking (drawer dimmed beneath); backdrop
//     rgba(26,26,26,0.55).
//   - NO X icon — only Cancel + Save are valid exits per workforce design
//     system §4 mandatory-reason convention (the reason field is required).
//   - Heading: "Edit Time Log".
//   - Subheading: dynamic per row state per §5.5 — default / approved-warning
//     / rejected-warning. Approved/Rejected variants add a [!] icon and
//     warning copy that the row stays in its current status; admin uses
//     PAY-005's existing [Undo] post-decision action to revert.
//   - Form: punch-in date+time, punch-out date+time, jobCode dropdown,
//     breaks sub-form, reason textarea (required ≥10 chars after trim,
//     ≤500 chars).
//   - Live "Preview changes" diff card between form and footer — old in
//     red strikethrough, new in green; unchanged fields rendered neutral.
//   - Save button DISABLED until: (a) at least one field changed,
//     (b) reason ≥10 chars after trim, (c) all field validations pass.
//   - Server error mapping per Marcus tech doc §4.5 (timelogAdminEditAPI).
//
// Authoritative inputs:
//   - Marcus tech doc §4.5 captured-curl JSON
//     (PUT /api/workforce/timelogs/:id/admin-edit)
//   - Zuri TIME-010 design spec §5.4 + §6.3
//   - Workforce design system tokens (inlined hex values per existing
//     components/staff/TimesheetReview.css convention)

import React, { useEffect, useMemo, useRef, useState } from "react";
import moment from "moment-timezone";
import { timelogAdminEditAPI } from "../../services/api";
import { jobCodeAPI } from "../../services/api";
import JobCodePicker from "./JobCodePicker";
import "./TimeLogEditModal.css";

const STUDIO_TZ = "America/Phoenix";
const REASON_MIN = 10;
const REASON_MAX = 500;

const ROW_STATUS = {
  PENDING: "pending",
  APPROVED: "approved",
  REJECTED: "rejected",
};

/**
 * Convert an ISO/Date to a Phoenix-tz {date: 'YYYY-MM-DD', time: 'HH:mm'}
 * pair suitable for native date+time inputs. Returns null fields for null
 * input.
 */
const isoToPhxDateTimeParts = (iso) => {
  if (!iso) return { date: "", time: "" };
  const m = moment.tz(iso, STUDIO_TZ);
  if (!m.isValid()) return { date: "", time: "" };
  return { date: m.format("YYYY-MM-DD"), time: m.format("HH:mm") };
};

/**
 * Combine a Phoenix-tz {date, time} pair back into an ISO-8601 datetime
 * string (UTC). Empty string in either field returns null.
 */
const phxDateTimePartsToIso = (date, time) => {
  if (!date || !time) return null;
  const m = moment.tz(`${date}T${time}`, STUDIO_TZ);
  if (!m.isValid()) return null;
  return m.toISOString();
};

/**
 * Format an ISO datetime as "h:mm A MST" for diff-display copy.
 */
const formatDiffTime = (iso) => {
  if (!iso) return "(none)";
  const m = moment.tz(iso, STUDIO_TZ);
  if (!m.isValid()) return "(invalid)";
  return `${m.format("h:mm A")} MST`;
};

/**
 * Map server error code from Marcus tech doc §4.5 to UI surface.
 *   inline_field — render under the named field
 *   modal_banner — render at top of modal body (e.g., 409 period_locked)
 *   page_toast   — bubble out via onResultErrorToast (e.g., 404 timelog_not_found)
 *
 * The mapper returns `{ surface, fieldKey?, message, accent? }` so the modal
 * can render appropriately. Unknown codes fall back to a generic toast.
 */
const mapServerError = (errResponse) => {
  const data = errResponse?.response?.data || {};
  const code = data.error || "server_error";
  const status = errResponse?.response?.status || 500;
  switch (code) {
    case "no_changes_supplied":
      return {
        surface: "modal_banner",
        message: "No changes detected. Edit a field or cancel.",
        accent: "neutral",
      };
    case "reason_too_short":
      return {
        surface: "inline_field",
        fieldKey: "reason",
        message: `Reason must be at least ${REASON_MIN} characters.`,
      };
    case "reason_too_long":
      return {
        surface: "inline_field",
        fieldKey: "reason",
        message: `Reason can be at most ${REASON_MAX} characters.`,
      };
    case "invalid_time_range":
      return {
        surface: "inline_field",
        fieldKey: "punchOut",
        message: "Punch-out must be after punch-in.",
      };
    case "invalid_field_shape":
      return {
        surface: "inline_field",
        fieldKey: data.field || "punchIn",
        message: data.field
          ? `Invalid value for ${data.field}.`
          : "Invalid field value.",
      };
    case "invalid_job_code":
      return {
        surface: "inline_field",
        fieldKey: "jobCode",
        message: "That job code is not active.",
      };
    case "period_locked":
      return {
        surface: "modal_banner",
        message:
          "Period was locked while you were editing. Refresh to see current state.",
        accent: "locked",
      };
    case "forbidden_admin_only":
      return {
        surface: "page_toast",
        message: "You don't have permission to edit time logs.",
      };
    case "timelog_not_found":
      return {
        surface: "page_toast",
        message: "This time log no longer exists.",
      };
    default:
      return {
        surface: "modal_banner",
        message:
          status >= 500
            ? "Something went wrong. Try again, or refresh the page."
            : "Couldn't save changes. Refresh and try again.",
        accent: "error",
      };
  }
};

/**
 * Per-row subheading copy per Zuri spec §5.5.
 */
const subheadingForStatus = (status) => {
  switch (status) {
    case ROW_STATUS.APPROVED:
      return {
        kind: "warning",
        text:
          "Direct edit on an APPROVED row. Changes will be logged but the row stays approved for payroll. Audit trail captures the change.",
      };
    case ROW_STATUS.REJECTED:
      return {
        kind: "warning",
        text:
          "Direct edit on a REJECTED row. Row stays rejected. Audit trail captures the change.",
      };
    default:
      return {
        kind: "default",
        text: "Direct edit. Changes are logged to the audit trail.",
      };
  }
};

/**
 * Compute field-level diffs for the live preview panel. Returns an array of
 * { field, label, oldDisplay, newDisplay, changed } in the order Zuri spec
 * §5.4 prescribes.
 */
const computeDiffs = (originalRow, draft) => {
  const diffs = [];

  const punchInChanged =
    draft.punchInIso && originalRow.punchInTime !== draft.punchInIso;
  diffs.push({
    field: "punchInTime",
    label: "Punch in",
    oldDisplay: formatDiffTime(originalRow.punchInTime),
    newDisplay: formatDiffTime(draft.punchInIso),
    changed: punchInChanged,
  });

  const punchOutChanged =
    (draft.punchOutIso || null) !== (originalRow.punchOutTime || null);
  diffs.push({
    field: "punchOutTime",
    label: "Punch out",
    oldDisplay: formatDiffTime(originalRow.punchOutTime),
    newDisplay: formatDiffTime(draft.punchOutIso),
    changed: punchOutChanged,
  });

  const jobCodeChanged =
    draft.jobCode !== "" && draft.jobCode !== (originalRow.jobCode || "");
  diffs.push({
    field: "jobCode",
    label: "Job code",
    oldDisplay: originalRow.jobCode || "(none)",
    newDisplay: draft.jobCode || "(none)",
    changed: jobCodeChanged,
  });

  // Breaks: shallow length-and-value compare. Server recomputes minutes; UI
  // just shows pre/post counts and the entries.
  const originalBreaks = Array.isArray(originalRow.breaks)
    ? originalRow.breaks
    : [];
  const breaksChanged =
    draft.breaks.length !== originalBreaks.length ||
    draft.breaks.some((b, i) => {
      const o = originalBreaks[i] || {};
      return (
        (b.startIso || null) !== (o.startTime || null) ||
        (b.endIso || null) !== (o.endTime || null) ||
        Boolean(b.isPaid) !== Boolean(o.isPaid)
      );
    });
  diffs.push({
    field: "breaks",
    label: "Breaks",
    oldDisplay:
      originalBreaks.length === 0
        ? "(none)"
        : originalBreaks
            .map(
              (b) =>
                `${formatDiffTime(b.startTime)} – ${formatDiffTime(b.endTime)} ${
                  b.isPaid ? "(paid)" : "(unpaid)"
                }`
            )
            .join("; "),
    newDisplay:
      draft.breaks.length === 0
        ? "(none)"
        : draft.breaks
            .map(
              (b) =>
                `${formatDiffTime(b.startIso)} – ${formatDiffTime(b.endIso)} ${
                  b.isPaid ? "(paid)" : "(unpaid)"
                }`
            )
            .join("; "),
    changed: breaksChanged,
  });

  return diffs;
};

const buildRequestBody = (originalRow, draft) => {
  const body = { reason: draft.reason.trim() };
  const punchInChanged =
    draft.punchInIso && originalRow.punchInTime !== draft.punchInIso;
  const punchOutChanged =
    (draft.punchOutIso || null) !== (originalRow.punchOutTime || null);
  const jobCodeChanged =
    draft.jobCode !== "" && draft.jobCode !== (originalRow.jobCode || "");
  const originalBreaks = Array.isArray(originalRow.breaks)
    ? originalRow.breaks
    : [];
  const breaksChanged =
    draft.breaks.length !== originalBreaks.length ||
    draft.breaks.some((b, i) => {
      const o = originalBreaks[i] || {};
      return (
        (b.startIso || null) !== (o.startTime || null) ||
        (b.endIso || null) !== (o.endTime || null) ||
        Boolean(b.isPaid) !== Boolean(o.isPaid)
      );
    });

  if (punchInChanged) body.punchInTime = draft.punchInIso;
  if (punchOutChanged) body.punchOutTime = draft.punchOutIso;
  if (jobCodeChanged) body.jobCode = draft.jobCode;
  if (breaksChanged) {
    body.breaks = draft.breaks.map((b) => ({
      startTime: b.startIso,
      endTime: b.endIso,
      isPaid: Boolean(b.isPaid),
    }));
  }
  return body;
};

const TimeLogEditModal = ({
  open,
  row,
  onCancel,
  onSaved,
  onPageToast,
}) => {
  const initialDraft = useMemo(() => {
    if (!row) {
      return {
        punchInDate: "",
        punchInTime: "",
        punchOutDate: "",
        punchOutTime: "",
        jobCode: "",
        breaks: [],
        reason: "",
      };
    }
    const inParts = isoToPhxDateTimeParts(row.punchInTime);
    const outParts = isoToPhxDateTimeParts(row.punchOutTime);
    return {
      punchInDate: inParts.date,
      punchInTime: inParts.time,
      punchOutDate: outParts.date,
      punchOutTime: outParts.time,
      jobCode: row.jobCode || "",
      breaks: (Array.isArray(row.breaks) ? row.breaks : []).map((b) => {
        const sParts = isoToPhxDateTimeParts(b.startTime);
        const eParts = isoToPhxDateTimeParts(b.endTime);
        return {
          startDate: sParts.date,
          startTime: sParts.time,
          endDate: eParts.date,
          endTime: eParts.time,
          startIso: b.startTime || null,
          endIso: b.endTime || null,
          isPaid: Boolean(b.isPaid),
        };
      }),
      reason: "",
    };
  }, [row]);

  const [draft, setDraft] = useState(initialDraft);
  const [submitting, setSubmitting] = useState(false);
  const [bannerError, setBannerError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [jobCodes, setJobCodes] = useState([]);
  const [jobCodesLoading, setJobCodesLoading] = useState(false);
  const [jobCodesError, setJobCodesError] = useState(false);
  const dialogRef = useRef(null);

  // Reset draft each time the modal opens with a (potentially different) row.
  useEffect(() => {
    if (open) {
      setDraft(initialDraft);
      setBannerError(null);
      setFieldErrors({});
    }
  }, [open, initialDraft]);

  // Load JobCodes once on mount of the modal so the picker has options.
  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setJobCodesLoading(true);
    setJobCodesError(false);
    jobCodeAPI
      .list({ active: true })
      .then((res) => {
        if (cancelled) return;
        const list = res?.data?.jobCodes || res?.data || [];
        setJobCodes(Array.isArray(list) ? list : []);
        setJobCodesLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        // Surface the failure to the picker so admin knows to refresh; keeps
        // the row's stale code visible regardless (see staleCode prop below).
        setJobCodes([]);
        setJobCodesLoading(false);
        setJobCodesError(true);
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  // Computed: punch-in/out ISO from the date+time pair.
  const punchInIso = phxDateTimePartsToIso(draft.punchInDate, draft.punchInTime);
  const punchOutIso = phxDateTimePartsToIso(
    draft.punchOutDate,
    draft.punchOutTime
  );

  // Update break ISOs alongside the date/time fields so diff and validation
  // can use them directly.
  const draftWithIsos = useMemo(
    () => ({
      ...draft,
      punchInIso,
      punchOutIso,
      breaks: draft.breaks.map((b) => ({
        ...b,
        startIso: phxDateTimePartsToIso(b.startDate, b.startTime),
        endIso: phxDateTimePartsToIso(b.endDate, b.endTime),
      })),
    }),
    [draft, punchInIso, punchOutIso]
  );

  const diffs = useMemo(() => {
    if (!row) return [];
    return computeDiffs(row, draftWithIsos);
  }, [row, draftWithIsos]);

  const anyChanged = diffs.some((d) => d.changed);
  const trimmedReason = draft.reason.trim();
  const reasonValid =
    trimmedReason.length >= REASON_MIN && trimmedReason.length <= REASON_MAX;

  // Time-ordering local check (defense in depth; server enforces too).
  const timeRangeValid = useMemo(() => {
    if (!punchInIso) return false;
    if (!punchOutIso) return true; // null punch-out tolerated for "still working"
    return new Date(punchOutIso).getTime() > new Date(punchInIso).getTime();
  }, [punchInIso, punchOutIso]);

  const canSave = anyChanged && reasonValid && timeRangeValid && !submitting;

  const subheading = subheadingForStatus(row?.status);

  const handleAddBreak = () => {
    setDraft((d) => ({
      ...d,
      breaks: [
        ...d.breaks,
        {
          startDate: d.punchInDate,
          startTime: "",
          endDate: d.punchInDate,
          endTime: "",
          startIso: null,
          endIso: null,
          isPaid: false,
        },
      ],
    }));
  };

  const handleRemoveBreak = (idx) => {
    setDraft((d) => ({
      ...d,
      breaks: d.breaks.filter((_, i) => i !== idx),
    }));
  };

  const handleBreakField = (idx, key, value) => {
    setDraft((d) => ({
      ...d,
      breaks: d.breaks.map((b, i) => (i === idx ? { ...b, [key]: value } : b)),
    }));
  };

  const handleSubmit = async () => {
    if (!row || !canSave) return;
    setSubmitting(true);
    setBannerError(null);
    setFieldErrors({});
    try {
      const body = buildRequestBody(row, draftWithIsos);
      const response = await timelogAdminEditAPI.edit(row._id, body);
      const updatedTimeLog = response?.data?.timeLog;
      if (!updatedTimeLog) {
        // Server contract contract violation; bubble out as toast.
        if (typeof onPageToast === "function") {
          onPageToast({
            kind: "error",
            message: "Server returned an unexpected response. Refresh.",
          });
        }
        setSubmitting(false);
        return;
      }
      setSubmitting(false);
      if (typeof onSaved === "function") onSaved(updatedTimeLog);
    } catch (err) {
      const mapping = mapServerError(err);
      setSubmitting(false);
      if (mapping.surface === "modal_banner") {
        setBannerError({ message: mapping.message, accent: mapping.accent });
        return;
      }
      if (mapping.surface === "inline_field") {
        setFieldErrors((prev) => ({
          ...prev,
          [mapping.fieldKey]: mapping.message,
        }));
        return;
      }
      // page_toast — close modal and bubble.
      if (typeof onPageToast === "function") {
        onPageToast({ kind: "error", message: mapping.message });
      }
      if (typeof onCancel === "function") onCancel();
    }
  };

  if (!open || !row) return null;

  return (
    <>
      <div
        className="tlem-backdrop"
        aria-hidden="true"
        data-testid="tlem-backdrop"
      />
      <div
        className="tlem-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="tlem-heading"
        ref={dialogRef}
        data-testid="tlem-modal"
      >
        <header className="tlem-header">
          <h2 id="tlem-heading" className="tlem-heading">
            Edit Time Log
          </h2>
          <p
            className={`tlem-subheading ${
              subheading.kind === "warning" ? "tlem-subheading-warning" : ""
            }`}
          >
            {subheading.kind === "warning" && (
              <span aria-hidden="true" className="tlem-warning-icon">
                !
              </span>
            )}
            {subheading.text}
          </p>
        </header>

        {bannerError && (
          <div
            className={`tlem-banner tlem-banner-${bannerError.accent || "error"}`}
            role="alert"
            data-testid="tlem-banner"
          >
            {bannerError.message}
          </div>
        )}

        <div className="tlem-body">
          {/* Punch-in field group */}
          <div className="tlem-field-group">
            <label className="tlem-label" htmlFor="tlem-punch-in-date">
              Punch in
            </label>
            <div className="tlem-datetime-pair">
              <input
                id="tlem-punch-in-date"
                type="date"
                value={draft.punchInDate}
                onChange={(e) =>
                  setDraft((d) => ({ ...d, punchInDate: e.target.value }))
                }
                className="tlem-input"
                data-testid="tlem-punch-in-date"
              />
              <input
                id="tlem-punch-in-time"
                type="time"
                value={draft.punchInTime}
                onChange={(e) =>
                  setDraft((d) => ({ ...d, punchInTime: e.target.value }))
                }
                className="tlem-input"
                data-testid="tlem-punch-in-time"
              />
            </div>
            <p className="tlem-helper">Phoenix time (MST).</p>
            {fieldErrors.punchIn && (
              <p className="tlem-field-error" role="alert">
                {fieldErrors.punchIn}
              </p>
            )}
          </div>

          {/* Punch-out field group */}
          <div className="tlem-field-group">
            <label className="tlem-label" htmlFor="tlem-punch-out-date">
              Punch out
            </label>
            <div className="tlem-datetime-pair">
              <input
                id="tlem-punch-out-date"
                type="date"
                value={draft.punchOutDate}
                onChange={(e) =>
                  setDraft((d) => ({ ...d, punchOutDate: e.target.value }))
                }
                className="tlem-input"
                data-testid="tlem-punch-out-date"
              />
              <input
                id="tlem-punch-out-time"
                type="time"
                value={draft.punchOutTime}
                onChange={(e) =>
                  setDraft((d) => ({ ...d, punchOutTime: e.target.value }))
                }
                className="tlem-input"
                data-testid="tlem-punch-out-time"
              />
            </div>
            <p className="tlem-helper">Phoenix time (MST). Leave blank for &quot;still working&quot;.</p>
            {!timeRangeValid && punchInIso && punchOutIso && (
              <p className="tlem-field-error" role="alert">
                Punch-out must be after punch-in.
              </p>
            )}
            {fieldErrors.punchOut && (
              <p className="tlem-field-error" role="alert">
                {fieldErrors.punchOut}
              </p>
            )}
          </div>

          {/* Job code picker (TIME-006 §3.1) — replaces the previous <select>.
              Stale-code preservation: if the row's existing jobCode is not in
              the active list, the picker still renders it as a selectable
              option (with "(inactive)" suffix) so admin can keep it. */}
          <div className="tlem-field-group">
            <label className="tlem-label" htmlFor="tlem-job-code">
              Job code
            </label>
            <JobCodePicker
              inputId="tlem-job-code"
              testId="tlem-job-code"
              size="md"
              codes={jobCodes}
              loading={jobCodesLoading}
              error={jobCodesError}
              value={draft.jobCode || null}
              onChange={(code) =>
                setDraft((d) => ({ ...d, jobCode: code || "" }))
              }
              staleCode={
                row.jobCode &&
                !jobCodes.some((j) => (j.code || j) === row.jobCode)
                  ? row.jobCode
                  : null
              }
              placeholder="(use primary role)"
              emptyState="disabled-pill"
              errorMessage={fieldErrors.jobCode || undefined}
            />
          </div>

          {/* Breaks sub-form */}
          <div className="tlem-field-group">
            <div className="tlem-breaks-header">
              <span className="tlem-label">Breaks</span>
              <button
                type="button"
                className="tlem-btn-link"
                onClick={handleAddBreak}
                data-testid="tlem-add-break"
              >
                + Add break
              </button>
            </div>
            {draft.breaks.length === 0 ? (
              <p className="tlem-empty">(no breaks)</p>
            ) : (
              <ul className="tlem-break-list">
                {draft.breaks.map((b, i) => (
                  <li key={i} className="tlem-break-row">
                    <div className="tlem-datetime-pair">
                      <input
                        type="date"
                        value={b.startDate}
                        onChange={(e) =>
                          handleBreakField(i, "startDate", e.target.value)
                        }
                        className="tlem-input tlem-input-sm"
                        aria-label="Break start date"
                      />
                      <input
                        type="time"
                        value={b.startTime}
                        onChange={(e) =>
                          handleBreakField(i, "startTime", e.target.value)
                        }
                        className="tlem-input tlem-input-sm"
                        aria-label="Break start time"
                      />
                      <span className="tlem-break-arrow" aria-hidden="true">
                        &rarr;
                      </span>
                      <input
                        type="date"
                        value={b.endDate}
                        onChange={(e) =>
                          handleBreakField(i, "endDate", e.target.value)
                        }
                        className="tlem-input tlem-input-sm"
                        aria-label="Break end date"
                      />
                      <input
                        type="time"
                        value={b.endTime}
                        onChange={(e) =>
                          handleBreakField(i, "endTime", e.target.value)
                        }
                        className="tlem-input tlem-input-sm"
                        aria-label="Break end time"
                      />
                    </div>
                    <label className="tlem-break-paid">
                      <input
                        type="checkbox"
                        checked={Boolean(b.isPaid)}
                        onChange={(e) =>
                          handleBreakField(i, "isPaid", e.target.checked)
                        }
                      />
                      Paid
                    </label>
                    <button
                      type="button"
                      className="tlem-btn-link tlem-btn-link-danger"
                      onClick={() => handleRemoveBreak(i)}
                      aria-label={`Remove break ${i + 1}`}
                    >
                      Remove
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Reason textarea */}
          <div className="tlem-field-group">
            <label className="tlem-label" htmlFor="tlem-reason">
              Reason for change <span className="tlem-required">*</span>
            </label>
            <textarea
              id="tlem-reason"
              className="tlem-textarea"
              value={draft.reason}
              onChange={(e) =>
                setDraft((d) => ({ ...d, reason: e.target.value }))
              }
              maxLength={REASON_MAX + 100}
              placeholder="Explain what changed and why."
              data-testid="tlem-reason"
              aria-required="true"
              aria-describedby="tlem-reason-help tlem-reason-counter"
            />
            <div className="tlem-reason-meta">
              <span id="tlem-reason-help" className="tlem-helper">
                Required. At least {REASON_MIN} characters. Appears in the audit
                trail and the staff member&apos;s &quot;View change history&quot;.
              </span>
              <span
                id="tlem-reason-counter"
                className={
                  trimmedReason.length > REASON_MAX
                    ? "tlem-counter tlem-counter-over"
                    : "tlem-counter"
                }
                aria-live="polite"
              >
                {trimmedReason.length} / {REASON_MAX}
              </span>
            </div>
            {fieldErrors.reason && (
              <p className="tlem-field-error" role="alert">
                {fieldErrors.reason}
              </p>
            )}
          </div>

          {/* Preview-changes diff */}
          <div
            className="tlem-preview"
            data-testid="tlem-preview"
            aria-live="polite"
          >
            <h3 className="tlem-preview-heading">Preview changes</h3>
            {!anyChanged ? (
              <p className="tlem-preview-empty">
                Edit a field above to see what will change.
              </p>
            ) : (
              <ul className="tlem-preview-list">
                {diffs.map((d) => (
                  <li
                    key={d.field}
                    className={
                      d.changed ? "tlem-diff-row tlem-diff-changed" : "tlem-diff-row"
                    }
                  >
                    <span className="tlem-diff-label">{d.label}:</span>{" "}
                    {d.changed ? (
                      <>
                        <span className="tlem-diff-old">{d.oldDisplay}</span>{" "}
                        &rarr;{" "}
                        <span className="tlem-diff-new">{d.newDisplay}</span>
                      </>
                    ) : (
                      <span className="tlem-diff-unchanged">
                        {d.oldDisplay} (unchanged)
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        <footer className="tlem-footer">
          <button
            type="button"
            className="tlem-btn tlem-btn-secondary"
            onClick={onCancel}
            disabled={submitting}
            data-testid="tlem-cancel"
          >
            Cancel
          </button>
          <button
            type="button"
            className="tlem-btn tlem-btn-primary"
            onClick={handleSubmit}
            disabled={!canSave}
            data-testid="tlem-save"
          >
            {submitting ? "Saving..." : "Save Changes"}
          </button>
        </footer>
      </div>
    </>
  );
};

export default TimeLogEditModal;
